import React, { useState } from 'react'
import Head, { Title } from '@fs/zion-head'
import { Body1, Body2, HeaderBlock, Button, useAtSize, ZionForm, TextField, Autosuggest } from '@fs/zion-ui'

import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { getAnonSession } from '@fs/zion-session'
import { useRegionLanguage, useCorrections } from '../../../aam/AppAam/hooks/hooks'
import { useDSCDispatch } from '../../../state/DSCStore'
import wifImage from './assets/WIF.jpg'
import wifIcon from './assets/wifIcon.svg'
import { setSessionStorageItem } from '../../helpers/sessionStorage'
import ConditionalHeader from '../ConditionalHeader/ConditionalHeader'

export default function LandingPage(props) {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const DSCDispatch = useDSCDispatch()
  const isCorrections = useCorrections()
  const [toggleGuestForm, setToggleGuestForm] = useState(!!isCorrections)
  const countryLanguageOptions = useRegionLanguage(t)
  const { guestFields } = props
  const {
    splashImage = wifImage,
    splashImageAlt = t('dlp.wiflandingpage.mainimage', 'where am I from - family looking at photos together'),
    icon = wifIcon,
    appName = 'Where am I From?: Sign Up',
    headerText = t('wif.landingpage.title', 'Where Am I From?'),
    buttonColor = '#026184',
    showAccountCreationText = false,
    signInHref,
    children,
    showSignIn = true,
    title = t('dlp.wiflandingpage.title', 'Where Am I From? | Explore Your Heritage'),
    description = t(
      'dlp.wiflandingpage.description',
      'Where are you from? Learn where you and your ancestors have lived and explore the cultures and traditions of those places.'
    ),
    content = 'https://www.familysearch.org/discovery/where-am-i-from',
  } = props

  setSessionStorageItem({ guestTree: [] })

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}` : null
  }

  // Awful nasty hack to prevent last name from zooming on Safari
  // https://stackoverflow.com/questions/2989263/disable-auto-zoom-in-input-text-tag-safari-on-iphone/6394497#6394497
  const textFieldNoZoomStyle = css`
    margin-top: 25px;
    input {
      font-size: 16px;
    }
  `

  const createAccountCSS = css`
    max-width: 570px;
  `
  const mainCSS = css`
    display: flex;
    flex-direction: column;
  `
  const imgCSS = css`
    width: 100%;
    max-height: 400px;
    object-fit: cover;
  `
  const iconAndTitleWrapperCSS = css`
    margin: 1vw;
    display: flex;
    color: white;
    position: absolute;
    height: 100%;
    font-family: 'museo', Verdana, Ayuthaya, 'HanaMinBFont', serif;
  `
  const bannerSectionCSS = css`
    position: relative;
  `
  const iconCSS = css`
    position: relative;
    top: 61%;
    left: 18%;
    height: 25%;
  `
  const headerCSS = css`
    display: ${atSize({ sm: 'block', lg: 'block' })};
    ${atSize({ default: 'margin-top:24px;', sm: 'margin-top:24px;' })}
  `
  const contentCSS = css`
    padding-left: 3.3%;
    padding-right: ${atSize({ default: '10px;', md: '20%;' })};
  `
  const buttonsCSS = css`
    display: flex;
    align-items: center;
    margin-top: 25px;
    margin-bottom: 5px;
    [emphasis='billboard'] {
      background: ${buttonColor} !important;
      color: white;
    }
    [emphasis='billboard']:hover {
      background: rgba(${hexToRgb(buttonColor)}, 0.7) !important;
    }
    [emphasis='billboard']:disabled {
      background: #ddd !important;
    }
    > * {
      margin-right: 20px;
    }
  `
  const customButtonCss = css`
    margin-top: auto;
    margin-bottom: auto;
    padding: 10px;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.2s;
    color: ${buttonColor};
    text-transform: uppercase;
    border: none;
    background: none;

    :hover {
      background: rgba(${hexToRgb(buttonColor)}, 0.1) !important;
    }
    :focus {
      outline: none;
    }
  `

  const validate = (values) => {
    const errors = {}
    if (guestFields.firstName && values.firstName?.trim() === '') {
      errors.firstName = t('aam-landing-validation-enter-first-name', 'Please enter your first name')
    }
    if (guestFields.lastName && values.lastName?.trim() === '') {
      errors.lastName = t('aam-landing-validation-enter-last-name', 'Please enter your last name')
    }
    if (!values.languageRegion && guestFields.languageRegion) {
      errors.languageRegion = t(
        'aam-landing-validation-select-country-language',
        'Please select a country and language'
      )
    }
    if (guestFields.birthYear) {
      if (!values.birthYear) {
        errors.birthYear = t('aam-landing-validation-enter-birthyear', 'Please enter your birth year')
      } else if (values.birthYear <= 1900) {
        errors.birthYear = t(
          'aam-landing-validation-enter-birthyear-after-1900',
          'Please enter a birth year after 1900'
        )
      } else if (values.birthYear > new Date().getFullYear()) {
        errors.birthYear = t('aam-landing-validation-enter-valid-birthyear', 'Please enter a valid birth year')
      }
    }
    return errors
  }

  const dispatchToGlobal = (values) => {
    getAnonSession()
      .then(() => {
        DSCDispatch({
          type: 'POPULATE_NONSTANDARD_USER',
          data: { nonstandardUser: { ...values } },
        })
      })
      .then(() => {
        if (window.document.URL.includes('about')) {
          window.location.reload()
        }
      })
  }

  const submitAnalytics = (countryLang) => {
    if (appName === 'All About Me' && countryLang) {
      trackEvent({
        link_action: `${appName}: submit`,
        allAboutMe_countryLanguage: countryLang,
      })
    } else {
      trackEvent({
        link_action: appName,
      })
    }
  }

  const passToGlobal = (values, formikBag) => {
    formikBag.resetForm()
    submitAnalytics(values.languageRegion)
    dispatchToGlobal(values)
  }

  const suppressAutocomplete = (event) => {
    event.target.setAttribute('autocomplete', 'disabled')
  }

  return (
    <div css={mainCSS}>
      <ConditionalHeader activeExperienceId="landing" />
      <Head>
        <meta property="og:description" content={description} />
        <link rel="canonical" content={content} />
      </Head>
      <Title>{title}</Title>
      <div css={bannerSectionCSS}>
        <div css={iconAndTitleWrapperCSS}>
          <img src={icon} css={iconCSS} alt={t('dlp.wiflandingpage.logo', 'Where am I from logo')} />
        </div>
        {!isCorrections && <img src={splashImage} css={imgCSS} alt={splashImageAlt} />}
      </div>
      <div css={contentCSS}>
        <div css={headerCSS}>
          <HeaderBlock size="xl" heading={headerText} />
          <Body1>{description}</Body1>
        </div>
        <div css={buttonsCSS}>
          {showSignIn && !isCorrections && (
            <>
              <Button emphasis="billboard" to={signInHref} linkName={`${appName}: sign in`} external>
                {t('dlp.wiflandingpage.signin', 'Sign In')}
              </Button>

              {(guestFields || isCorrections) && (
                <button
                  type="button"
                  data-testid="Guest Login"
                  css={customButtonCss}
                  onClick={() => {
                    trackEvent({ link_action: `DS_${appName}: CONTINUE_AS_GUEST` })
                    const noFields = Object.keys(guestFields).length > 0
                    return !noFields ? dispatchToGlobal() : setToggleGuestForm(!toggleGuestForm)
                  }}
                >
                  {t('dlp.landingpagecomponent.guest', 'Continue as guest')}
                </button>
              )}
            </>
          )}
        </div>
        {toggleGuestForm && (
          <>
            {showAccountCreationText && (
              <div css={createAccountCSS}>
                <Body2>
                  {t(
                    'dlp.wiflandingpage.underbuttonsubtext',
                    `Don’t have an account? No problem. Creating an account is free and easy. Before using the experience, try to enter at least four generations of information in your family tree.`
                  )}
                </Body2>
              </div>
            )}

            <ZionForm onSubmit={passToGlobal} validate={validate}>
              {({ handleReset, isValid, ...formProps }) => (
                <div css={textFieldNoZoomStyle}>
                  {guestFields.firstName && (
                    <TextField
                      name="firstName"
                      placeholder={t('dlp.landingpage.firstname', 'Your first name')}
                      value={formProps.values.firstName}
                    />
                  )}
                  {guestFields.lastName && (
                    <TextField
                      name="lastName"
                      placeholder={t('dlp.landingpage.lastname', 'Your last name')}
                      value={formProps.values.lastName}
                    />
                  )}
                  {guestFields.birthYear && (
                    <TextField
                      name="birthYear"
                      placeholder={t('dlp.landingpage.birthyear', 'Birth Year')}
                      value={formProps.values.birthYear}
                      type="number"
                      helpMessage=""
                    />
                  )}
                  {guestFields.languageRegion && (
                    <Autosuggest
                      suggestions={countryLanguageOptions}
                      value={formProps.values.languageRegion}
                      name="languageRegion"
                      inputProps={{
                        onFocus: suppressAutocomplete,
                      }}
                      placeholder={t('aam-landing-validation-select-country-language')}
                    />
                  )}
                  <div css={buttonsCSS}>
                    <Button type="submit" external="true" emphasis="billboard" disabled={!isValid}>
                      {t('common-ui:submit.action', 'Submit')}
                    </Button>
                    {!isCorrections && (
                      <Button
                        onClick={() => {
                          setToggleGuestForm(!toggleGuestForm)
                        }}
                      >
                        {' '}
                        {t('common-ui:cancel.action', 'Cancel')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </ZionForm>
          </>
        )}
        {children}
      </div>
    </div>
  )
}
